// ============================================
// VOLUNTEER MODULE - VOLUNTEERS PAGE
// Fully themed via useTheme() — no hardcoded colors
// ============================================

import React, { useState, useEffect } from "react";
import { useVolunteerData } from "../components/VolunteerDataContext";
import { useTheme } from "../components/ThemeContext";
import {
  getVolunteers, createVolunteer, updateVolunteer,
  deleteVolunteer, importVolunteers, downloadVolunteerTemplate, getEvents,
} from "../services/api";

const SKILLS_OPTIONS = ["First Aid","Language Skills","Technical Support","Driving","Coordination","Customer Service"];
const DESIGNATION_OPTIONS = ["Joint Secretary","Joint Treasurer","Member","Secretary","Treasurer"];

// ── MULTI CHIP SELECT ──────────────────────
function MultiChip({ label, options, selected, onChange, C }) {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(s=>s!==v) : [...selected, v]);
  return (
    <div>
      <div style={{ fontSize:11, color:C.muted, marginBottom:6, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>{label}</div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
        {options.map((opt,i) => (
          <div key={i} onClick={() => toggle(opt)}
            style={{ padding:"5px 12px", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:600, transition:"all 0.15s", background:selected.includes(opt)?C.accentSoft:C.inputBg, border:`1px solid ${selected.includes(opt)?C.accent:C.border}`, color:selected.includes(opt)?C.accent:C.muted }}>
            {selected.includes(opt) ? "✓ " : ""}{opt}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── VOLUNTEER FORM ─────────────────────────
function VolunteerForm({ initial, events, onSave, onCancel, saving, message, C }) {
  const empty = { eventId:"", itsId:"", fullName:"", password:"", jamiat:"", jamaat:"", designation:"", age:"", zone:"", whatsappNumber:"", coordinator:"", email:"", phoneNumber:"", address1:"", address2:"", city:"", state:"", zipCode:"", country:"", emergencyName:"", emergencyPhone:"", preferredDutyTypes:"", arrivalDate:"", departureDate:"", availabilityNotes:"", registrationStatus:"pending" };
  const [form, setForm]   = useState(initial || empty);
  const [skills, setSkills] = useState(initial?.skills || []);

  useEffect(() => { if(initial){ setForm(initial); setSkills(initial.skills||[]); } }, [initial]);

  const s = (k,v) => setForm(f=>({...f,[k]:v}));
  const inp = (k) => (e) => s(k, e.target.value);

  const inpStyle = { width:"100%", padding:"9px 12px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:8, color:C.text, fontSize:13, outline:"none", boxSizing:"border-box" };
  const selStyle = { ...inpStyle, cursor:"pointer" };

  // ── Use regular functions NOT React components
  // This prevents focus loss on every keystroke
  const sh = (title) => (
    <div style={{ gridColumn:"1 / -1", borderBottom:`1px solid ${C.border}`, paddingBottom:7, marginTop:6 }}>
      <div style={{ fontSize:12, fontWeight:700, color:C.accent }}>{title}</div>
    </div>
  );

  const fld = (label, k, placeholder, type="text", required=false) => (
    <div>
      <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>
        {label}{required && <span style={{color:C.red}}> *</span>}
      </div>
      <input type={type} value={form[k]||""} onChange={inp(k)} placeholder={placeholder} style={inpStyle} />
    </div>
  );

  const sel = (label, k, options, required=false) => (
    <div>
      <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>
        {label}{required && <span style={{color:C.red}}> *</span>}
      </div>
      <select value={form[k]||""} onChange={inp(k)} style={selStyle}>
        <option value="">Select...</option>
        {options.map((o,i) => <option key={i} value={o.value||o}>{o.label||o}</option>)}
      </select>
    </div>
  );

  return (
    <div>
      {message && (
        <div style={{ padding:"12px 16px", borderRadius:10, marginBottom:14, background:message.type==="success"?C.greenSoft:C.redSoft, border:`1px solid ${message.type==="success"?C.green:C.red}44`, color:message.type==="success"?C.green:C.red, fontSize:13, fontWeight:600 }}>
          {message.text}
        </div>
      )}

      <div className="form-grid-2">
        {sh("📅 Event Assignment")}
        <div style={{ gridColumn:"1 / -1" }}>
          {sel("Assign to Event", "eventId", events.map(e=>({value:e.id,label:e.name})), true)}
        </div>

        {sh("👤 Personal Information")}
        {/* ITS ID — 8 digits only, unique */}
        <div>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>
            ITS ID (8 digits) <span style={{color:C.red}}>*</span>
          </div>
          <input
            value={form.itsId||""}
            onChange={e=>s("itsId", e.target.value.replace(/\D/g,"").slice(0,8))}
            placeholder="12345678"
            maxLength={8}
            style={{ ...inpStyle, letterSpacing:"0.1em", fontFamily:"monospace",
              borderColor: form.itsId && form.itsId.length===8 ? C.green : form.itsId ? C.orange : C.border
            }}
          />
          {form.itsId && form.itsId.length!==8 && (
            <div style={{ fontSize:10, color:C.orange, marginTop:3 }}>
              {form.itsId.length}/8 digits entered
            </div>
          )}
        </div>
        {fld("Full Name", "fullName", "e.g. Ahmed Al Rashidi", "text", true)}
        {fld("Password", "password", "Account password", "password", !initial)}
        {fld("Email", "email", "volunteer@email.com", "email", true)}
        {fld("WhatsApp Number", "whatsappNumber", "+971501234567", "text", true)}
        {fld("Phone Number", "phoneNumber", "+971501234567", "text", true)}
        {fld("Jamiat", "jamiat", "e.g. Mumbai Jamiat", "text", true)}
        {fld("Jamaat", "jamaat", "e.g. Al Noor Jamaat", "text", true)}
        {sel("Designation", "designation", DESIGNATION_OPTIONS, true)}
        {fld("Zone", "zone", "e.g. North Zone", "text", true)}
        {fld("Age", "age", "e.g. 28", "number")}
        {fld("Coordinator", "coordinator", "Coordinator name")}

        {sh("📍 Address")}
        <div style={{ gridColumn:"1 / -1" }}>{fld("Address Line 1", "address1", "Street address")}</div>
        <div style={{ gridColumn:"1 / -1" }}>{fld("Address Line 2", "address2", "Apt, Suite, etc.")}</div>
        {fld("City", "city", "e.g. Dubai")}
        {fld("State", "state", "e.g. Dubai")}
        {fld("Zip Code", "zipCode", "e.g. 00000")}
        {fld("Country", "country", "e.g. UAE")}

        {sh("🆘 Emergency Contact")}
        {fld("Contact Name", "emergencyName", "Emergency contact name")}
        {fld("Contact Phone", "emergencyPhone", "+971501234567")}

        {sh("📋 Details & Status")}
        <div style={{ gridColumn:"1 / -1" }}>{fld("Preferred Duty Types", "preferredDutyTypes", "e.g. Gate Control, Crowd Management")}</div>
        <div style={{ gridColumn:"1 / -1" }}>
          <MultiChip label="Skills" options={SKILLS_OPTIONS} selected={skills} onChange={setSkills} C={C} />
        </div>

        {/* Availability fields are event-specific — shown only on Events > Volunteers page */}
        {sel("Registration Status", "registrationStatus", [{value:"pending",label:"Pending"},{value:"approved",label:"Approved"},{value:"rejected",label:"Rejected"}])}
      </div>

      <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20, flexWrap:"wrap" }}>
        {onCancel && <button onClick={onCancel} style={{ padding:"10px 18px", background:"none", border:`1px solid ${C.border}`, borderRadius:10, color:C.muted, fontSize:13, fontWeight:600, cursor:"pointer" }}>Cancel</button>}
        <button onClick={() => {
          const reqFields = ["fullName","itsId","email","whatsappNumber","phoneNumber","jamiat","jamaat","designation","zone"];
          const labels = { fullName:"Full Name", itsId:"ITS ID", email:"Email", whatsappNumber:"WhatsApp", phoneNumber:"Phone", jamiat:"Jamiat", jamaat:"Jamaat", designation:"Designation", zone:"Zone" };
          for(const k of reqFields) { if(!form[k]) { alert(`${labels[k]} is required.`); return; } }
          if(form.itsId.length!==8||!/^\d+$/.test(form.itsId)) { alert("ITS ID must be exactly 8 digits."); return; }
          onSave({...form, skills});
        }} disabled={saving||!form.fullName||!form.itsId||!form.email}
          style={{ padding:"10px 24px", background:(form.fullName&&form.itsId&&form.email)?C.accent:C.border, border:"none", borderRadius:10, color:(form.fullName&&form.itsId&&form.email)?"#000":C.muted, fontSize:13, fontWeight:700, cursor:"pointer" }}>
          {saving ? "Saving..." : "✓ Save Volunteer"}
        </button>
      </div>
    </div>
  );
}

// ── PROFILE MODAL ──────────────────────────
function ProfileModal({ volunteer, events, onClose, onSaved, C }) {
  const [mode, setMode]     = useState("view");
  const [saving, setSaving] = useState(false);
  const [message, setMsg]   = useState(null);
  const { bump }            = useVolunteerData();

  const handleSave = async (data) => {
    setSaving(true);
    try {
      await updateVolunteer(volunteer.id, data);
      setMsg({ type:"success", text:"✅ Profile updated!" });
      setTimeout(() => { setMsg(null); setMode("view"); onSaved(); }, 900);
    } catch { setMsg({ type:"error", text:"Failed to update." }); }
    setSaving(false);
  };

  const regColor = (s) => ({ arrived:C.green, pending:C.orange, not_coming:C.red, approved:C.green, rejected:C.red }[s] || C.muted);
  const regLabel = (s) => ({ arrived:"Arrived", pending:"Pending", not_coming:"Not Coming", approved:"Approved", rejected:"Rejected" }[s] || s || "pending");

  const Row = ({ label, value, color }) => (
    <div style={{ display:"flex", gap:12, padding:"8px 0", borderBottom:`1px solid ${C.border}` }}>
      <div style={{ fontSize:12, color:C.muted, fontWeight:600, minWidth:150 }}>{label}</div>
      <div style={{ fontSize:13, color:color||C.text, flex:1 }}>{value||"—"}</div>
    </div>
  );

  return (
    <div className="modal-overlay" style={{ background:C.modalBg }}>
      <div className="modal-box" style={{ background:C.card, border:`1px solid ${C.border}` }}>
        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, position:"sticky", top:0, background:C.card, paddingBottom:12, borderBottom:`1px solid ${C.border}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:42, height:42, borderRadius:"50%", background:C.accentSoft, border:`2px solid ${C.accent}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:800, color:C.accent }}>
              {volunteer.fullName?.charAt(0)||"V"}
            </div>
            <div>
              <div style={{ fontSize:15, fontWeight:800, color:C.text }}>{volunteer.fullName}</div>
              <div style={{ fontSize:12, color:C.muted }}>ITS: {volunteer.itsId} · {volunteer.designation||"Volunteer"}</div>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {mode==="view" && (
              <button onClick={() => setMode("edit")}
                style={{ padding:"7px 14px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:8, color:C.accent, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                ✏️ Edit
              </button>
            )}
            {mode==="edit" && (
              <button onClick={() => { setMode("view"); setMsg(null); }}
                style={{ padding:"7px 14px", background:"none", border:`1px solid ${C.border}`, borderRadius:8, color:C.muted, fontSize:12, cursor:"pointer" }}>
                ← View
              </button>
            )}
            <button onClick={onClose} style={{ background:"none", border:"none", color:C.muted, fontSize:22, cursor:"pointer", lineHeight:1 }}>✕</button>
          </div>
        </div>

        {mode === "view" ? (
          <div>
            <div style={{ display:"flex", gap:6, marginBottom:16, flexWrap:"wrap" }}>
              <span className="vh-badge" style={{ background:`${regColor(volunteer.registrationStatus)}22`, color:regColor(volunteer.registrationStatus), border:`1px solid ${regColor(volunteer.registrationStatus)}44` }}>{volunteer.registrationStatus||"pending"}</span>
              {volunteer.eventName && <span className="vh-badge" style={{ background:`${C.accent}22`, color:C.accent, border:`1px solid ${C.accent}44` }}>{volunteer.eventName}</span>}
              {volunteer.jamiat && <span className="vh-badge" style={{ background:`${C.purple}22`, color:C.purple, border:`1px solid ${C.purple}44` }}>{volunteer.jamiat}</span>}
            </div>

            <div style={{ fontSize:11, fontWeight:700, color:C.accent, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:6 }}>Personal</div>
            <Row label="ITS ID"         value={volunteer.itsId} />
            <Row label="Email"          value={volunteer.email} />
            <Row label="WhatsApp"       value={volunteer.whatsappNumber} color={C.green} />
            <Row label="Phone"          value={volunteer.phoneNumber} />
            <Row label="Jamiat"         value={volunteer.jamiat} />
            <Row label="Jamaat"         value={volunteer.jamaat} />
            <Row label="Designation"    value={volunteer.designation} />
            <Row label="Zone"           value={volunteer.zone} />
            <Row label="Coordinator"    value={volunteer.coordinator} />

            <div style={{ fontSize:11, fontWeight:700, color:C.accent, textTransform:"uppercase", letterSpacing:"0.5px", marginTop:16, marginBottom:6 }}>Address</div>
            <Row label="Address"        value={[volunteer.address1,volunteer.address2].filter(Boolean).join(", ")} />
            <Row label="City / State"   value={[volunteer.city,volunteer.state].filter(Boolean).join(", ")} />
            <Row label="Country"        value={[volunteer.zipCode,volunteer.country].filter(Boolean).join(", ")} />

            <div style={{ fontSize:11, fontWeight:700, color:C.accent, textTransform:"uppercase", letterSpacing:"0.5px", marginTop:16, marginBottom:6 }}>Emergency</div>
            <Row label="Contact Name"   value={volunteer.emergencyName} />
            <Row label="Contact Phone"  value={volunteer.emergencyPhone} />

            {/* Availability fields shown on event page, not here */}

            {(volunteer.skills||[]).length > 0 && (
              <div style={{ marginTop:16 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.accent, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:6 }}>Skills</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                  {(volunteer.skills||[]).map((sk,i) => (
                    <span key={i} className="vh-badge" style={{ background:`${C.green}22`, color:C.green, border:`1px solid ${C.green}44` }}>{sk}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <VolunteerForm initial={volunteer} events={events} onSave={handleSave} onCancel={() => { setMode("view"); setMsg(null); }} saving={saving} message={message} C={C} />
        )}
      </div>
    </div>
  );
}

// ── CREATE TAB ─────────────────────────────
function CreateTab({ events, onSaved, C }) {
  const [saving, setSaving] = useState(false);
  const [message, setMsg]   = useState(null);
  const { bump } = useVolunteerData();

  const handleSave = async (data) => {
    setSaving(true);
    try {
      await createVolunteer(data);
      setMsg({ type:"success", text:`✅ Volunteer "${data.fullName}" created!` });
      setTimeout(() => setMsg(null), 2000);
      bump();                         // ← sync all pages
      onSaved && onSaved();
    } catch(err) {
      const msg = err.response?.data?.message || "Failed to create volunteer.";
      setMsg({ type:"error", text: msg });
    }
    setSaving(false);
  };

  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:"24px 28px" }}>
      <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:18 }}>👤 New Volunteer</div>
      <VolunteerForm events={events} onSave={handleSave} saving={saving} message={message} C={C} />
    </div>
  );
}

// ── IMPORT TAB ─────────────────────────────
function ImportTab({ onSaved, C }) {
  const [importing, setImporting] = useState(false);
  const [message, setMsg]         = useState(null);
  const { bump } = useVolunteerData();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if(!file) return;
    setImporting(true); setMsg(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await importVolunteers(fd);
      setMsg({ type:"success", text:res.data.message });
      bump();                         // ← sync all pages
      onSaved && onSaved();
    } catch { setMsg({ type:"error", text:"Import failed." }); }
    setImporting(false);
    e.target.value = "";
  };

  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:"24px 28px" }}>
      <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:6 }}>📥 Import Volunteers</div>
      <div style={{ fontSize:13, color:C.muted, marginBottom:20 }}>
        Supported: <span className="vh-badge" style={{ background:`${C.accent}22`, color:C.accent, border:`1px solid ${C.accent}44` }}>.xlsx</span>{" "}
        <span className="vh-badge" style={{ background:`${C.accent}22`, color:C.accent, border:`1px solid ${C.accent}44` }}>.xls</span>{" "}
        <span className="vh-badge" style={{ background:`${C.accent}22`, color:C.accent, border:`1px solid ${C.accent}44` }}>.csv</span>
      </div>

      {message && (
        <div style={{ padding:"12px 16px", borderRadius:10, marginBottom:16, background:message.type==="success"?C.greenSoft:C.redSoft, border:`1px solid ${message.type==="success"?C.green:C.red}44`, color:message.type==="success"?C.green:C.red, fontSize:13, fontWeight:600 }}>
          {message.text}
        </div>
      )}

      {[
        { num:1, title:"Download the import template", sub:"Contains all required columns with a sample row.", action:<button onClick={downloadVolunteerTemplate} style={{ padding:"9px 18px", background:C.orangeSoft, border:`1px solid ${C.orange}44`, borderRadius:9, color:C.orange, fontSize:13, fontWeight:600, cursor:"pointer" }}>📋 Download Template</button> },
        { num:2, title:"Fill in the template", sub:<>Required: <strong style={{color:C.text}}>ITS ID, Full Name, Email, WhatsApp Number</strong>. All other columns optional.</> },
        { num:3, title:"Upload your file", sub:"Select your filled .xlsx, .xls or .csv file.", action:
          <label style={{ padding:"9px 18px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:9, color:C.accent, fontSize:13, fontWeight:700, cursor:"pointer", display:"inline-block" }}>
            {importing ? "⏳ Importing..." : "📥 Choose File & Import"}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={{ display:"none" }} />
          </label>
        },
      ].map((step,i) => (
        <div key={i} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:12, padding:"16px 20px", marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
            <div style={{ width:28, height:28, borderRadius:"50%", background:C.accentSoft, border:`1px solid ${C.accent}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800, color:C.accent, flexShrink:0 }}>
              {step.num}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:4 }}>{step.title}</div>
              <div style={{ fontSize:12, color:C.muted, marginBottom:step.action?12:0 }}>{step.sub}</div>
              {step.action}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── LIST TAB ───────────────────────────────
function ListTab({ refresh, events, C }) {
  const [volunteers, setVols] = useState([]);
  const [loading, setLoad]    = useState(false);
  const [search, setSearch]   = useState("");
  const [filterStatus, setFS] = useState("");
  const [filterEvent, setFE]  = useState("");
  const [profileVol, setPV]   = useState(null);

  const { version, bump } = useVolunteerData();

  // Refresh whenever:
  //   • the local refresh prop changes (after creating/importing in a sibling tab)
  //   • the global version bumps (something changed on another page)
  useEffect(() => { load(); }, [refresh, version]);

  const load = async () => {
    setLoad(true);
    try { const r = await getVolunteers(); setVols(r.data.volunteers||[]); }
    catch { setVols([]); }
    setLoad(false);
  };

  const handleDelete = async (id, name) => {
    if(!window.confirm(`Delete volunteer "${name}"?`)) return;
    try {
      await deleteVolunteer(id);
      bump();                         // ← sync all pages
      load();
    } catch { alert("Failed to delete."); }
  };

  const filtered = volunteers.filter(v => {
    const q = search.toLowerCase();
    const mSearch = !q || v.fullName?.toLowerCase().includes(q) || v.itsId?.includes(q) || v.email?.toLowerCase().includes(q) || v.whatsappNumber?.includes(q);
    const mStatus = !filterStatus || v.registrationStatus === filterStatus;
    const mEvent  = !filterEvent  || v.eventId === filterEvent;
    return mSearch && mStatus && mEvent;
  });

  const regColor = (s) => ({ arrived:C.green, pending:C.orange, not_coming:C.red, approved:C.green, rejected:C.red }[s] || C.muted);
  const regLabel = (s) => ({ arrived:"Arrived", pending:"Pending", not_coming:"Not Coming", approved:"Approved", rejected:"Rejected" }[s] || s || "pending");

  return (
    <>
      {profileVol && (
        <ProfileModal volunteer={profileVol} events={events} C={C} onClose={() => setPV(null)} onSaved={() => { load(); setPV(null); }} />
      )}

      {/* Filters */}
      <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap" }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍  Search by name, ITS ID, email or WhatsApp..."
          style={{ flex:1, minWidth:200, padding:"9px 13px", background:C.card, border:`1px solid ${C.border}`, borderRadius:9, color:C.text, fontSize:13, outline:"none" }} />
        <select value={filterStatus} onChange={e=>setFS(e.target.value)}
          style={{ padding:"9px 13px", background:C.card, border:`1px solid ${C.border}`, borderRadius:9, color:filterStatus?C.text:C.muted, fontSize:13, outline:"none", cursor:"pointer" }}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select value={filterEvent} onChange={e=>setFE(e.target.value)}
          style={{ padding:"9px 13px", background:C.card, border:`1px solid ${C.border}`, borderRadius:9, color:filterEvent?C.text:C.muted, fontSize:13, outline:"none", cursor:"pointer" }}>
          <option value="">All Events</option>
          {events.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </div>

      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, overflow:"hidden" }}>
        {loading ? (
          <div style={{ padding:40, textAlign:"center", color:C.muted }}>⏳ Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding:60, textAlign:"center" }}>
            <div style={{ fontSize:40, marginBottom:14 }}>👥</div>
            <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:6 }}>No volunteers found</div>
            <div style={{ fontSize:13, color:C.muted }}>Import a file or add volunteers manually.</div>
          </div>
        ) : (
          <>
            <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, fontSize:12, color:C.muted }}>
              Showing <strong style={{color:C.text}}>{filtered.length}</strong> of {volunteers.length} · Click a name to view full profile
            </div>
            <div className="responsive-table">
              <table>
                <thead>
                  <tr style={{ background:C.bg }}>
                    {["ITS ID","Name","Event","Jamiat","Jamaat","Designation","Zone","WhatsApp","Phone","Email","Status","Actions"].map(h => (
                      <th key={h} style={{ padding:"10px 13px", textAlign:"left", fontSize:10, fontWeight:700, color:C.muted, letterSpacing:"0.8px", textTransform:"uppercase", borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((v,i) => (
                    <tr key={i} style={{ borderBottom:`1px solid ${C.border}`, transition:"background 0.15s" }}
                      onMouseEnter={e=>e.currentTarget.style.background=C.border+"44"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{ padding:"11px 13px", fontSize:11, color:C.muted, fontFamily:"monospace", whiteSpace:"nowrap" }}>{v.itsId||"—"}</td>
                      <td style={{ padding:"11px 13px" }}>
                        <button onClick={() => setPV(v)} style={{ background:"none", border:"none", cursor:"pointer", textAlign:"left", padding:0 }}>
                          <div style={{ fontSize:13, color:C.accent, fontWeight:700, textDecoration:"underline", textDecorationStyle:"dotted", textUnderlineOffset:3 }}>{v.fullName}</div>
                          {v.email && <div style={{ fontSize:11, color:C.muted, marginTop:1 }}>{v.email}</div>}
                        </button>
                      </td>
                      <td style={{ padding:"11px 13px", fontSize:12, color:C.text, whiteSpace:"nowrap" }}>{v.eventName||"—"}</td>
                      <td style={{ padding:"11px 13px", fontSize:12, color:C.text }}>{v.jamiat||"—"}</td>
                      <td style={{ padding:"11px 13px", fontSize:12, color:C.text }}>{v.jamaat||"—"}</td>
                      <td style={{ padding:"11px 13px" }}>
                        {v.designation
                          ? <span className="vh-badge" style={{ background:`${C.purple}22`, color:C.purple, border:`1px solid ${C.purple}44` }}>{v.designation}</span>
                          : <span style={{ fontSize:11, color:C.muted }}>—</span>}
                      </td>
                      <td style={{ padding:"11px 13px", fontSize:12, color:C.text }}>{v.zone||"—"}</td>
                      <td style={{ padding:"11px 13px" }}>
                        {v.whatsappNumber
                          ? <span style={{ fontSize:12, color:C.green, fontWeight:600, whiteSpace:"nowrap" }}>💬 {v.whatsappNumber}</span>
                          : <span style={{ fontSize:11, color:C.muted }}>—</span>}
                      </td>
                      <td style={{ padding:"11px 13px", fontSize:12, color:C.muted }}>{v.phoneNumber||"—"}</td>
                      <td style={{ padding:"11px 13px", fontSize:11, color:C.muted }}>{v.email||"—"}</td>
                      <td style={{ padding:"11px 13px" }}>
                        <span className="vh-badge" style={{ background:`${regColor(v.registrationStatus)}22`, color:regColor(v.registrationStatus), border:`1px solid ${regColor(v.registrationStatus)}44` }}>
                          {regLabel(v.registrationStatus)}
                        </span>
                      </td>
                      <td style={{ padding:"11px 13px" }}>
                        <div style={{ display:"flex", gap:5 }}>
                          <button onClick={() => setPV(v)}
                            style={{ padding:"5px 10px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:7, color:C.accent, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                            👁 View
                          </button>
                          <button onClick={() => handleDelete(v.id, v.fullName)}
                            style={{ padding:"5px 10px", background:C.redSoft, border:`1px solid ${C.red}33`, borderRadius:7, color:C.red, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── MAIN PAGE ──────────────────────────────
export default function Volunteers() {
  const { C } = useTheme();
  const [tab, setTab]           = useState("list");
  const [refreshCount, setRC]   = useState(0);
  const [events, setEvents]     = useState([]);

  useEffect(() => {
    getEvents().then(r => setEvents(r.data.events||[])).catch(()=>{});
  }, []);

  const refresh = () => { setRC(c=>c+1); setTab("list"); };

  const TABS = [
    { id:"list",   label:"👥 All Volunteers" },
    { id:"create", label:"➕ Add Manually" },
    { id:"import", label:"📥 Import File" },
  ];

  return (
    <div>
      <div className="flex-between page-header" style={{ marginBottom:20 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, color:C.text, fontFamily:"Georgia, serif" }}>Volunteers</div>
          <div style={{ fontSize:13, color:C.muted, marginTop:4 }}>Import files or add volunteers manually. Click a name to view full profile.</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:3, marginBottom:20, background:C.card, padding:4, borderRadius:12, width:"fit-content", border:`1px solid ${C.border}` }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding:"8px 18px", background:tab===t.id?C.accent:"transparent", border:"none", borderRadius:9, color:tab===t.id?"#000":C.muted, fontSize:13, fontWeight:tab===t.id?700:500, cursor:"pointer", transition:"all 0.2s", whiteSpace:"nowrap" }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "list"   && <ListTab   refresh={refreshCount} events={events} C={C} />}
      {tab === "create" && <CreateTab events={events} onSaved={refresh} C={C} />}
      {tab === "import" && <ImportTab onSaved={refresh} C={C} />}
    </div>
  );
}